import { Button, Drawer, Flex, Typography, Modal } from "antd";
import { FC, useState } from "react";
import { useIntl } from "react-intl";
import { useMessage } from "shared/libs/hooks/useMessage/useMessage";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { setActivity } from "shared/config/store/reducers/appSlice";
import { useDeleteAccountMutation } from "../api";
import { useLogoutMutation } from "entities/user";
import { ApiError } from "shared/model/types";

type Props = {
  open: boolean;
  onClose: () => unknown;
};

const DeleteAccountDrawer: FC<Props> = ({ open, onClose }) => {
  const { formatMessage: t } = useIntl();
  const { showError, showSuccess } = useMessage();
  const dispatch = useDispatch();
  const [deleteAccount] = useDeleteAccountMutation();
  const [logout] = useLogoutMutation();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const handleDeleteClick = () => {
    setIsConfirmationOpen(true);
  };

  const handleConfirmationClose = () => {
    setIsConfirmationOpen(false);
  };

  const handleConfirmDelete = () => {
    dispatch(setActivity(true));
    deleteAccount({})
      .unwrap()
      .then(() => {
        showSuccess?.(t({ id: "screens.setting.delete.account.success" }));
        handleConfirmationClose();
        onClose();
        logout();
      })
      .catch((err: ApiError) => {
        console.log(err);
        showError?.(t({ id: "screens.setting.delete.account.error" }));
      })
      .finally(() => {
        dispatch(setActivity(false));
      });
  };

  return (
    <>
      <Drawer
        title={t({ id: "screens.setting.delete.account.title" })}
        open={open}
        closable
        onClose={onClose}
        width={560}
      >
        <Flex vertical align="center" gap={16}>
          <ExclamationCircleOutlined
            style={{
              fontSize: 36,
              padding: 18,
              width: "fit-content",
              boxSizing: "content-box",
              background: "rgba(255, 0, 0, 0.1)",
              borderRadius: 100,
              marginBottom: 15,
              color: "#ff4d4f",
            }}
          />

          <Typography.Title
            level={4}
            style={{ textAlign: "center", margin: 0 }}
          >
            {t({ id: "screens.setting.delete.account.warning.title" })}
          </Typography.Title>

          <Typography.Text type="secondary" style={{ textAlign: "center" }}>
            {t({ id: "screens.setting.delete.account.warning.subtitle" })}
          </Typography.Text>

          <Flex vertical gap={8} style={{ width: "100%", marginTop: 16 }}>
            <Typography.Text>
              • {t({ id: "screens.setting.delete.account.warning.note1" })}
            </Typography.Text>
            <Typography.Text>
              • {t({ id: "screens.setting.delete.account.warning.note2" })}
            </Typography.Text>
            <Typography.Text>
              • {t({ id: "screens.setting.delete.account.warning.note3" })}
            </Typography.Text>
          </Flex>

          <Flex
            vertical
            gap={8}
            style={{
              width: "100%",
              backgroundColor: "#e6f4ff",
              padding: 16,
              borderRadius: 8,
              marginTop: 16,
            }}
          >
            <Typography.Text strong style={{ color: "#1677ff" }}>
              {t({ id: "screens.setting.delete.account.dataRetention.title" })}
            </Typography.Text>
            <Typography.Text style={{ color: "#1677ff", fontSize: 12 }}>
              {t({ id: "screens.setting.delete.account.dataRetention.message" })}
            </Typography.Text>
          </Flex>

          <Button
            danger
            type="primary"
            icon={<DeleteOutlined />}
            onClick={handleDeleteClick}
            style={{ marginTop: 24, width: "100%" }}
          >
            {t({ id: "buttons.delete" })}
          </Button>
        </Flex>
      </Drawer>

      <Modal
        title={t({ id: "screens.setting.delete.account.confirmation.title" })}
        open={isConfirmationOpen}
        onCancel={handleConfirmationClose}
        footer={null}
        centered
      >
        <Flex vertical gap={24}>
          <Typography.Text>
            {t({ id: "screens.setting.delete.account.confirmation.message" })}
          </Typography.Text>

          <Flex
            gap={12}
            justify="center"
            align="center"
            style={{ marginTop: 24 }}
          >
            <Button type="default" onClick={handleConfirmationClose}>
              {t({ id: "button.simple.cancel" })}
            </Button>
            <Button danger type="primary" onClick={handleConfirmDelete}>
              {t({ id: "screens.setting.delete.account.confirmation.button.delete" })}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};

export default DeleteAccountDrawer;
